import {
  CardFamilyAnswer,
  DataSourcesTabsCard,
  NoDataInfo,
  AssistenceInfoCard,
} from 'components'
import DataSourcesColumn from 'components/DataSourcesColumn'
import { Device } from 'components/Device'
import { useFamily } from 'context/hooks/useFamily'
import { addressMask, cpfMask } from 'utils/masks'
import { handleAnswer } from 'utils/familyUtils'

import NoAlertsIcon from 'assets/icons/noAlertsIcon.svg'
import IconAlert from 'assets/icons/infoIconLarge.svg'

import React, { useState } from 'react'
import { type FamilyContextType } from 'types/services/Family'
import useQuery from 'context/hooks/useQuery'
import CardContentInfosGeneral from 'components/CardContentInfosGeneral'
import InconsistentIcon from 'components/Icons/InconsistentIcon'
import { useAccessibility } from 'context/hooks/useAccessibility'

import './styles.scss'
function General() {
  const device = Device.useDevice()
  const { families, alertsPerBases, inconsistencies } = useFamily()
  const devicePrefix = device.prefix()

  const { contrastIsSelected } = useAccessibility()

  const [currentBase, setCurrentBase] = useState(0)

  const { basesQuery, basesNamesList, basesIds, cpf, nis } = useQuery()

  function decideSetenceSeparator(index: number, lengthListValues: number) {
    if (index === lengthListValues - 1) {
      return ''
    } else if (index === lengthListValues - 2) {
      return ' e '
    }
    return ', '
  }

  function getAssistences(answer: string | boolean) {
    if (answer === 'Sim') return ['Recebeu recentemente']
    else if (answer === true) return ['Não recebeu esse auxílio recentemente']
    return ['Não tem informação']
  }

  function noticeOfinconsistenciesGenerated() {
    const listValues = inconsistencies?.familyInfo.filter((value) =>
      ['birthDate', 'gender', 'name'].includes(value ?? '')
    )
    const lengthListValues = listValues?.length

    const valuesGenerator = [
      Boolean(listValues?.includes('name')) && 'nome',
      Boolean(listValues?.includes('birthDate')) && 'data de nascimento',
      Boolean(listValues?.includes('gender')) && 'gênero',
    ].filter((value) => value)

    const styleNoticeOfinconsistencies = {
      backgroundColor: !contrastIsSelected ? '#FFF5D6' : '',
      width: 'fit-content',
      borderRadius: '5px',

      padding: '0 5px 3px 5px',

      marginTop: '1rem',
    }

    return lengthListValues !== undefined && lengthListValues > 0 ? (
      <div style={styleNoticeOfinconsistencies}>
        {<InconsistentIcon isHighContrast={contrastIsSelected} />}
        {valuesGenerator[0] === 'data de nascimento' ? `A ` : `O  `}
        {valuesGenerator.map((value, index) => {
          return (
            <React.Fragment key={`${index.toExponential}`}>
              <strong>{value}</strong>
              {decideSetenceSeparator(index, lengthListValues)}
            </React.Fragment>
          )
        })}
        {` da pessoa responsável pela unidade familiar ${
          lengthListValues === 1 ? 'está' : 'estão'
        } inconsistente entre as bases`}
      </div>
    ) : (
      <></>
    )
  }

  return (
    <main className={`container-general ${devicePrefix ?? 'undefined'}`}>
      <section className="container-general__content">
        <DataSourcesTabsCard
          titles={basesNamesList}
          currentBase={currentBase}
          setCurrentBase={setCurrentBase}
          noticeOfInconsistencies={noticeOfinconsistenciesGenerated()}
        >
          {families.map(({ base, family }: FamilyContextType) =>
            basesIds[currentBase] === base && family != null ? (
              <React.Fragment key={base}>
                <DataSourcesColumn title="Alertas">
                  {alertsPerBases[base]?.length > 0 ? (
                    alertsPerBases[base].map((recommendation) => {
                      if (recommendation !== null) {
                        const url = `/integrasocial/pesquisa/${
                          recommendation.keySection
                        }?cpf=${cpf}&nis=${nis}&bases=${JSON.stringify(
                          basesQuery
                        )}`
                        return (
                          <CardFamilyAnswer
                            url={url}
                            key={
                              recommendation?.question +
                              recommendation?.answer +
                              base
                            }
                            question={recommendation.question}
                            answer={handleAnswer(
                              recommendation.answer,
                              recommendation.type
                            )}
                            isAlert={recommendation.isAlert}
                          />
                        )
                      } else return null
                    })
                  ) : (
                    <NoDataInfo
                      iconSrc={NoAlertsIcon}
                      title="Família sem alertas"
                      minWidth="400px"
                      showIcon
                    />
                  )}
                </DataSourcesColumn>

                <div className="infos-base">
                  <DataSourcesColumn title="Informações gerais na base">
                    <CardContentInfosGeneral>
                      <h1>Documentação</h1>
                      <div>
                        <h2>Código Familiar</h2>
                        <p>{handleAnswer(family.familyCode)}</p>
                      </div>
                      <div>
                        <h2>CPF do responsável domiciliar</h2>
                        <p>{cpfMask(family.familyHead.cpf)}</p>
                      </div>
                      <div>
                        <h2>NIS do responsável domiciliar</h2>
                        <p>{handleAnswer(family.familyHead.nis)}</p>
                      </div>
                    </CardContentInfosGeneral>
                    <CardContentInfosGeneral
                      isNoticeOfInconsistencies={
                        (inconsistencies?.residence?.includes('address') ??
                          false) ||
                        (inconsistencies?.familyInfo.includes('city') ??
                          false) ||
                        inconsistencies?.familyInfo.includes('cep')
                      }
                    >
                      <h1>Endereço</h1>
                      <p style={{ whiteSpace: 'pre-line' }}>
                        {(inconsistencies?.residence?.includes('address') ??
                          false) && (
                          <InconsistentIcon
                            isNoticeOfInconsistenciesTooltip
                            isHighContrast={contrastIsSelected}
                          />
                        )}
                        {addressMask(family.residenceInfo?.address?.answer)}
                      </p>
                      <div>
                        <h2>
                          {(inconsistencies?.familyInfo.includes('city') ??
                            false) && (
                            <InconsistentIcon
                              isNoticeOfInconsistenciesTooltip
                              isHighContrast={contrastIsSelected}
                            />
                          )}
                          Cidade
                        </h2>
                        <p>
                          {handleAnswer(family.residenceInfo?.city?.answer)}
                        </p>
                      </div>
                      <div>
                        <h2>
                          {(inconsistencies?.familyInfo.includes('cep') ??
                            false) && (
                            <InconsistentIcon
                              isNoticeOfInconsistenciesTooltip
                              isHighContrast={contrastIsSelected}
                            />
                          )}
                          CEP
                        </h2>
                        <p>{handleAnswer(family.residenceInfo?.cep?.answer)}</p>
                      </div>
                      <div>
                        <h2>Zona</h2>
                        <p>
                          {handleAnswer(family.residenceInfo?.zone?.answer)}
                        </p>
                      </div>
                    </CardContentInfosGeneral>
                    <CardContentInfosGeneral
                      isNoticeOfInconsistencies={inconsistencies?.familyInfo.includes(
                        'phone'
                      )}
                    >
                      <h1>Contato</h1>
                      <div>
                        <h2>
                          {(inconsistencies?.familyInfo.includes('phone') ??
                            false) && (
                            <InconsistentIcon
                              isNoticeOfInconsistenciesTooltip
                              isHighContrast={contrastIsSelected}
                            />
                          )}
                          Telefone
                        </h2>
                        <p>{handleAnswer(family.phone)}</p>
                      </div>
                      <div>
                        <h2>E-mail</h2>
                        <p>{handleAnswer(family.email)}</p>
                      </div>
                    </CardContentInfosGeneral>
                  </DataSourcesColumn>

                  <div className={`list-cards ${devicePrefix ?? 'undefined'}`}>
                    <CardContentInfosGeneral>
                      <h1>Atualização de dados</h1>

                      <div>
                        <h2>Última atualização:</h2>
                        <p>{family?.collectDate?.toString()}</p>
                      </div>
                    </CardContentInfosGeneral>
                    {basesIds[currentBase] === 'agents' && (
                      <div className="list-cards__content">
                        <h3>Programas e auxílios</h3>
                        <div className="list-cards__content--infos">
                          {families.map((family) => {
                            if (family.base === 'agents') {
                              return (
                                <React.Fragment key={family?.base}>
                                  <AssistenceInfoCard
                                    title="CRAS"
                                    assistences={getAssistences(
                                      family?.family?.assistenceInfo
                                        ?.crasActivity?.answer
                                    )}
                                  />
                                  <AssistenceInfoCard
                                    title="CREAS"
                                    assistences={getAssistences(
                                      family?.family?.assistenceInfo
                                        ?.creasActivity?.answer
                                    )}
                                  />
                                  <AssistenceInfoCard
                                    title="Governo do Estado do Ceará"
                                    assistences={family?.family?.assistenceInfo?.govenmentAid?.answer?.split(
                                      /\s*;\s*/
                                    )}
                                  />
                                </React.Fragment>
                              )
                            }
                            return null
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </React.Fragment>
            ) : (
              <div style={{ margin: '0 auto' }} key={base}>
                <NoDataInfo
                  iconSrc={IconAlert}
                  title="Não há informações dessa família para essa base"
                  showIcon
                />
              </div>
            )
          )}
        </DataSourcesTabsCard>
      </section>
    </main>
  )
}

export default General
